/*
 *   File : notification.js
 *   Description : notification component
 *   Integrations : notification/get-received-notification
 *                  notification/read-all
 *                  notification/read
 */

import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { EmptyScreen, Loader } from "../../Components/Other";
import { PrimaryButton } from "../../Components/Buttons";
import { APIService } from "../../Service";
import { setPageTitle, setSearchEnabled } from "../../Redux/Ui/ui.slice";
import Icon from "../../Components/icons";
import { NotificationModal } from "../../Components/Modals";

export default function AllNotification() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setPageTitle(["Notifications"]));
    dispatch(setSearchEnabled(false));
  }, [dispatch]);

  /* -----------------------------------
      STATE MANAGEMENT
  ----------------------------------- */
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [modal, setModal] = useState(false);
  const [curNotification, setCurNotification] = useState(null);

  useEffect(() => {
    _getNotificationList();
  }, []);

  /* -----------------------------------
      API SERVICE
  ----------------------------------- */
  const _getNotificationList = () => {
    setLoader(true);

    APIService(true, "notification/get-received-notification", {
      page_num: 1,
      page_size: 10,
    })
      .then((res) => {
        if (res?.status_code === 200) {
          const notifications = res?.data?.data || [];
          setData(notifications);
          setIsEmpty(notifications.length === 0);
        } else {
          setData([]);
          setIsEmpty(true);
        }
      })
      .catch((err) => {
        console.error("Notification error:", err);
        setData([]);
        setIsEmpty(true);
      })
      .finally(() => setLoader(false));
  };

  const _markAllAsRead = () => {
    APIService(false, "notification/read-all")
      .then((res) => {
        if (res?.status_code === 200) {
          // Mark all notifications as read locally
          setData((prev) => prev.map((item) => ({ ...item, is_read: true })));
        }
      })
      .catch((err) => {
        console.error("Mark all as read error:", err);
      });
  };

 const _markAsRead = (item, index) => {
    // Open modal regardless of read status
    setCurNotification(item);
    setModal(true);

    // Already read — skip API call
    if (item?.is_read) return;

    APIService(true, "notification/read", {
      notification_id: item?.id,
    })
      .then((res) => {
        if (res?.status_code === 200) {
          // Mark specific notification as read locally
          setData((prev) =>
            prev.map((n, i) => (i === index ? { ...n, is_read: true } : n)),
          );
        }
      })
      .catch((err) => {
        console.error("Mark as read error:", err);
      });
  };

  /* -----------------------------------
      HELPERS
  ----------------------------------- */
  const formatDate = (date) => {
    if (!date) return "";

    const d = new Date(date);

    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  /* -----------------------------------
      RENDER
  ----------------------------------- */
  return (
    <div className="d-flex w-100 flex-column">
      {loader ? (
        <Loader />
      ) : isEmpty ? (
        <EmptyScreen message="No Notifications Found" />
      ) : (
        <div className="e-glass-card rounded-24 p-24 mt-3 shadow-none bg-white-43">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="fw-500 fs-18 lh-20 text-eerie-black mb-0">
              All notifications
            </h3>

            <PrimaryButton
              ClassName="bg-transparent border-0 p-0 fs-12 fw-500 text-facebook-blue text-decoration-underline"
              name="Mark all as read"
              onPress={_markAllAsRead}
            />
          </div>

          {/* Notification List */}
          <div className="d-flex flex-column">
            {data.map((item, index) => (
              <div key={item.id || index} className={`d-flex gap-3 px-3 py-3`}>
                {/* Icon */}
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center position-relative flex-shrink-0"
                  style={{
                    width: "40px",
                    height: "40px",
                    background: "#E3EFFF",
                  }}
                >
                  <Icon
                    icon="notification"
                    size={14}
                    className="text-facebook-blue e-goal-icon opacity-100"
                  />

                  {!item?.is_read && (
                    <span
                      className="position-absolute rounded-circle"
                      style={{
                        width: "10px",
                        height: "10px",
                        background: "#D31515",
                        border: "2px solid white",
                        top: "0px",
                        right: "3px",
                      }}
                    />
                  )}
                </div>

                {/* Content */}
                <div className="d-flex flex-column row-gap-2 flex-grow-1">
                  <h6 className="m-0 fs-14 text-capitalize fw-500 text-black e-primary-font-rubik">
                    {item?.title || "Notification"}
                  </h6>

                  <p className="m-0 fs-14 fw-400 text-granite-gray text-capitalize e-primary-font-rubik">
                    {item?.message || item?.description || "-"}
                  </p>

                  <PrimaryButton
                    ClassName="bg-transparent border-0 p-0 text-start fs-12 fw-500 text-facebook-blue text-decoration-underline w-auto"
                    name="View"
                    onPress={() => _markAsRead(item, index)}
                  />

                  <span className="fs-10 fw-400 text-davys-gray e-primary-font-rubik text-capitalize">
                    {formatDate(item?.created_at || item?.created)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <NotificationModal
        title={curNotification?.title}
        open={modal}
        time={"3m ago"}
        onCancel={() => setModal(false)}
        content={curNotification?.description}
      />
    </div>
  );
}
